import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Columns,
  DottedText,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Box, Grid } from '@mui/material';
import Typography from '@HomaHealth/ui-component-library/lib/_patient-access-components/atoms/Typography';
import React from 'react';
import { getXaxisLabels } from '../../core-utils/helper-utils/utilizattionProcedureHelper';
import {
  ICostBreakDownData,
  IPharmacyCostTrendData,
  ITopDrugsResponse,
} from '.';
import { numberToCurrencyFormatter } from '../../core-utils/helper-utils/helper';
import componentConstants from '../../core-utils/messages/componentConstants';
import {
  dropDownMenuItemsAlphabets,
  dropDownMenuItemsValues,
} from '../../core-utils/helper-utils/tableSortHelper';
import { DottedText as DottedText1 } from '../../core-utils/helper-utils/financialForensicsHelper';

export const utilizationRxConstants = {
  currencySymbol: {
    dollar: '$',
  },
  breadCrumbsConstant: [
    [
      'text',
      'Utilization Summary',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
    ],
    [
      'text',
      'Pharmacy Utilization',
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ],
};

const cellStyles = {
  fontSize: '14px',
  lineHeight: '23px',
  color: patientAccessTheme.palette.text.mediumemphasis,
  fontWeight: 400,
};

const pageConstants = componentConstants.PAGES.UTILIZATION_RX;

export const topDrugsTableColumn: Columns[] = [
  {
    name: 'Medication',
    align: 'left',
    cellStyles: { ...cellStyles, width: '200px' },
    sortColumnName: 'medication',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsAlphabets,
    width: '200px',
    showEllipsis: true,
  },
  {
    name: 'Therapeutic Class',
    align: 'left',
    cellStyles: { ...cellStyles, width: '200px' },
    sortColumnName: 'atcLevel',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsAlphabets,
    width: '200px',
    showEllipsis: true,
  },
  {
    name: 'Brand/Generic',
    align: 'left',
    cellStyles: cellStyles,
    sortColumnName: 'drugType',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsAlphabets,
    width: '195px',
  },
  {
    name: 'Total Cost',
    align: 'right',
    cellStyles: { ...cellStyles, fontWeight: 500 },
    sortColumnName: 'totalCost',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
    width: '200px',
  },
  {
    name: 'Cost Change YAG',
    align: 'right',
    cellStyles: cellStyles,
    sortColumnName: 'costChange',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
    width: '251px',
  },
  {
    name: 'Claimants',
    align: 'center',
    cellStyles: cellStyles,
    sortColumnName: 'claimantsCount',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
    width: '180px',
  },
  {
    name: 'Claims',
    align: 'left',
    cellStyles: cellStyles,
    sortColumnName: 'claimsCount',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
    width: '175px',
  },
];

export const topDrugsTableColumnsForMedPharma: Columns[] = [
  topDrugsTableColumn[0],
  ...topDrugsTableColumn.slice(3),
];

export const topTherapeuticsTableColumn: Columns[] = [
  {
    name: 'Therapeutic Class',
    align: 'left',
    cellStyles: { ...cellStyles, width: '400px' },
    sortColumnName: 'therapeuticClass',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsAlphabets,
    width: 'auto',
    showEllipsis: true,
  },

  {
    name: 'Total Cost',
    align: 'right',
    cellStyles: { ...cellStyles, fontWeight: 500 },
    sortColumnName: 'totalCost',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
    width: 'auto',
  },

  {
    name: 'Claimants',
    align: 'center',
    cellStyles: cellStyles,
    sortColumnName: 'claimantsCount',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
    width: 'auto',
  },
  {
    name: 'Claims',
    align: 'center',
    cellStyles: cellStyles,
    sortColumnName: 'claimsCount',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
    width: 'auto',
  },
];

export const getAxisLabelTypography = (
  label: string,
  positionProps: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  },
) => {
  return (
    <Grid style={{ position: 'absolute', ...positionProps }}>
      <Typography
        fontSize="12px"
        lineHeight="23px"
        textColor={patientAccessTheme.palette.text.highemphasis}
      >
        {label}
      </Typography>
    </Grid>
  );
};

export const getLegendItem = (
  color: string,
  value: string,
  isDottedText?: boolean,
  onClick?: () => void,
) => {
  return (
    <Grid
      style={{
        display: 'flex',
        alignItems: 'center',
        margin: '0px 24px 0px 0px',
      }}
    >
      <Grid
        style={{
          backgroundColor: color,
          height: '8px',
          width: '8px',
          borderRadius: '2px',
          marginRight: '8px',
        }}
      ></Grid>
      <Grid>
        <Box onClick={onClick}>
          {isDottedText ? (
            <DottedText
              text={value}
              typographyStyles={{
                fontSize: '12px',
                lineHeight: '24px',
                fontWeight: 400,
              }}
            />
          ) : (
            <Typography
              style={{
                fontSize: '12px',
                lineHeight: '24px',
                fontWeight: 'normal',
                color: patientAccessTheme.palette.text.mediumemphasis,
              }}
            >
              {value}
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export const formatPharmacyCostTrendData = (
  data: any,
): IPharmacyCostTrendData => {
  const medpharmaDrugsData: { value: number }[] = [];
  const specialityDrugsData: { value: number }[] = [];
  const otherPrescriptionDrugsData: { value: number }[] = [];
  let xAxisValues: { label: string }[] = [];

  const formattedData = data?.costTrend?.filter(
    (item: any) => item.totalCost !== 0,
  );

  formattedData?.map(
    (item: { totalCost: number; drugType: string; month: number }) => {
      switch (item?.drugType) {
        case pageConstants.DRUG_TYPES.MEDPHARMA:
          medpharmaDrugsData.push({ value: item?.totalCost });
          break;
        case pageConstants.DRUG_TYPES.SPECIALITY:
          specialityDrugsData.push({ value: item?.totalCost });
          break;
        case pageConstants.DRUG_TYPES.OTHER:
          otherPrescriptionDrugsData.push({ value: item?.totalCost });
          break;
      }
    },
  );

  const uniqueMonthYearMap = new Map();

  formattedData?.forEach((item: any) => {
    const key = `${item.month}-${item.year}`;
    if (!uniqueMonthYearMap.has(key)) {
      uniqueMonthYearMap.set(key, {
        label: getXaxisLabels(item.month, item.year),
      });
    }
  });
  xAxisValues = Array.from(uniqueMonthYearMap?.values());

  // let currentMonth = data?.startFromMonth;
  // let currentYear = data?.startFromYear;
  // const yearChangeValueOfI = 12 - data?.startFromMonth + 1;

  // for (let i = 0; i < 12; i++) {
  //   if (i >= yearChangeValueOfI) {
  //     currentYear = data?.startFromYear + 1;
  //   }
  //   xAxisValues.push({ label: getXaxisLabels(currentMonth, currentYear) });
  //   currentMonth = (currentMonth % 12) + 1;
  // }

  return {
    medpharmaDrugsData,
    specialityDrugsData,
    otherPrescriptionDrugsData,
    xAxisValues,
  };
};

export const formatCostBreakDownData = (
  data: {
    totalCost: number;
    drugType: string;
  }[],
) => {
  const costBreakDownData: ICostBreakDownData = {
    medPharmaDrugsCost: 0,
    otherPrescriptionDrugsCost: 0,
    specialityDrugsCost: 0,
    totalCost: 0,
  };

  let total = 0;
  data?.map((item) => {
    switch (item?.drugType) {
      case pageConstants.DRUG_TYPES.MEDPHARMA:
        costBreakDownData.medPharmaDrugsCost = item?.totalCost;
        break;
      case pageConstants.DRUG_TYPES.SPECIALITY:
        costBreakDownData.specialityDrugsCost = item?.totalCost;
        break;
      case pageConstants.DRUG_TYPES.OTHER:
        costBreakDownData.otherPrescriptionDrugsCost = item?.totalCost;
        break;
    }
    total += item?.totalCost;
  });
  costBreakDownData.totalCost = total;

  return costBreakDownData;
};

export const formatTopDrugsData = (
  data: ITopDrugsResponse[],
  isMedPharma?: boolean,
  isDiseasePrevalence?: boolean,
  onClick?: any,
) => {
  return data?.map((item) => {
    if (isDiseasePrevalence) {
      return [
        item?.medication ? item?.medication : '-',
        item?.drugType ? item?.drugType : '-',
        '$ ' + numberToCurrencyFormatter(item?.totalCost, false),
        item?.claimantsCount ? item?.claimantsCount : '-',
      ];
    } else {
      const key =
        item.claimType === 'rx' ? 'standard_drug_name' : 'procedure_desc_01';
      const medication = item.medication ? (
        onClick ? (
          <DottedText1
            text={item?.medication}
            onClick={() => onClick(item.medication, key)}
          />
        ) : (
          item?.medication
        )
      ) : (
        '-'
      );
      return isMedPharma
        ? [
            medication,
            '$ ' + numberToCurrencyFormatter(item?.totalCost, false),
            item?.costChange ? item?.costChange.toFixed(2) + '%' : '-',
            item?.claimantsCount ? item?.claimantsCount : '-',
            item?.claimsCount ? item?.claimsCount : '-',
          ]
        : [
            medication,
            item?.atcLevel ? item.atcLevel : '-',
            item?.drugType ? item?.drugType : '-',
            '$ ' + numberToCurrencyFormatter(item?.totalCost, false),
            item?.costChange ? item?.costChange.toFixed(2) + '%' : '-',
            item?.claimantsCount ? item?.claimantsCount : '-',
            item?.claimsCount ? item?.claimsCount : '-',
          ];
    }
  });
};
